import { createContext, useEffect, useState, memo, useRef } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  signInWithCredential,
  FacebookAuthProvider,
  sendEmailVerification,
  linkWithCredential,
  EmailAuthProvider,
  linkWithPopup,
  AuthProvider as FirebaseAuthProvider,
} from 'firebase/auth';
import {
  deleteObject,
  ref,
  uploadBytes,
  getDownloadURL,
  // getStorage, ref as storageRef,
} from 'firebase/storage';
import {
  Timestamp,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  // arrayUnion
} from 'firebase/firestore';
import {
  firestorage,
  firestore,
  googleAuthProvider,
  twitterAuthProvider,
} from '../utils/firebase';
import {
  extendedKeys,
  FormMode,
  UserCreateProfileFormType,
  UserFormType,
  UserSignUpFormType,
} from '../views/creatorOnBoarding/signup.types';
import { modifyUserData, updateMessagesData } from '../utils/helper';

import {
  cloneVoice,
  deleteVoice,
  sendWelcomeEmail,
  sendWelcomeFromContestEmail,
  updatePersona,
  uploadFacebookAuthResponse,
} from '../utils/api';
import { TIER_LEVEL_CONVERSION, USER_TYPE } from '../config/const';
import { useDraftPersona } from './DraftPersonaContext';
import useAnalyticsEventTracker from '../hooks/useAnalyticsEventTracker';
import { LoginModalVisibleType } from '../views/loginModal';
import { toast } from 'react-toastify';
import CONFIG from '../config/config';
import { logging } from '../utils/logger';

const enableIntroMessages = process.env.REACT_APP_USE_INTRO_MESSAGES === 'true';
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

// Initialize Firebase Authentication and get a reference to the service
interface AuthCtxType {
  authUser: any;
  setAuthUser: (userData: any) => void;
  token: string | null;
  updateUserProfileData: (
    userData: UserFormType,
    formMode: FormMode
  ) => Promise<any>;
  signInWithEmailPassword: (email: string, password: string, contest?: boolean) => Promise<any>;
  checkUserExistWithUsername: (userName: string) => Promise<boolean>;
  checkUserExistWithEmail: (email: string) => Promise<boolean>;
  logoutUser: () => Promise<void>;
  handleSendResetPasswordEmail: (email: string) => Promise<void>;
  // checkUserProfileActivation: (documentId: string) => Promise<any>;
  signInSignUpWithGoogle: () => Promise<any>;
  signInSignUpWithFacebook: (userData: any, messages?: any) => Promise<any>;
  signInSignUpWithTwitter: () => Promise<void>;
  authLoading: boolean;
  isAnonymousUser: boolean;
  setIsAnonymousUser: (value: boolean) => void;
  anonymousChat: any;
  setAnonymousChat: (value: any) => void;
  resetAnonymousData: any;
  setResetAnonymousData: (value: any) => void;
  generateUserName: (email: string) => Promise<string>;
  createUserAccountWithEmailPassword: (
    userData: UserSignUpFormType
  ) => Promise<any>;
  updateCreatedProfileData: (
    userData: UserCreateProfileFormType,
    formMode: FormMode
  ) => Promise<any>;
  showProfileCreated: any;
  setShowProfileCreated: (value: any) => void;
  updateVerifiedStatus: (userId: string, verified: boolean) => Promise<void>;
  fetchVerifiedStatus: (userId: string) => Promise<boolean>;
  fetchAndSetAuthUserData: (userId: string, accessToken: string, getAll?: boolean) => Promise<void>;
  loginModalVisible: LoginModalVisibleType;
  setLoginModalVisible: React.Dispatch<React.SetStateAction<LoginModalVisibleType>>;
  referredBy: React.RefObject<string>;
}

export const AuthContext = createContext<AuthCtxType>({
  authUser: null,
  setAuthUser: () => {
    return {} as any;
  },
  token: null,
  authLoading: false,
  updateUserProfileData: async () => {
    return {} as any;
  },
  signInWithEmailPassword: async () => {
    return {} as any;
  },
  checkUserExistWithUsername: async () => {
    return {} as boolean;
  },
  checkUserExistWithEmail: async () => {
    return {} as boolean;
  },
  logoutUser: async () => {
    return;
  },
  handleSendResetPasswordEmail: () => {
    return {} as any;
  },
  // checkUserProfileActivation: () => {
  //   return {} as any;
  // },
  signInSignUpWithGoogle: () => {
    return {} as any;
  },
  signInSignUpWithFacebook: () => {
    return {} as any;
  },
  signInSignUpWithTwitter: () => {
    return {} as any;
  },
  isAnonymousUser: null,
  setIsAnonymousUser: () => {
    return {} as any;
  },
  anonymousChat: null,
  setAnonymousChat: () => {
    return {} as any;
  },
  resetAnonymousData: null,
  setResetAnonymousData: () => {
    return {} as any;
  },
  generateUserName: async () => {
    return {} as string;
  },
  createUserAccountWithEmailPassword: async () => {
    return {} as any;
  },
  updateCreatedProfileData: async () => {
    return {} as any;
  },
  showProfileCreated: null,
  setShowProfileCreated: () => {
    return {} as any;
  },
  updateVerifiedStatus: async (userId: string, verified: boolean) => {
    return {} as any;
  },
  fetchVerifiedStatus: async (userId: string) => {
    return {} as any;
  },
  fetchAndSetAuthUserData: async (userId: string, accessToken: string, getAll?: boolean) => {
    return {} as any;
  },
  loginModalVisible: null,
  setLoginModalVisible: () => {
    return {} as any;
  },
  referredBy: null,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = getAuth();
  const [token, setToken] = useState<string | null>();
  const [authUser, setAuthUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [isAnonymousUser, setIsAnonymousUser] = useState<boolean>();
  const [anonymousChat, setAnonymousChat] = useState<any>(null);
  const [resetAnonymousData, setResetAnonymousData] = useState<boolean>(false);
  const [showProfileCreated, setShowProfileCreated] = useState<boolean>(false);
  const [loginModalVisible, setLoginModalVisible] =
    useState<LoginModalVisibleType>({ isOpen: false, contest: false, formType: 'signin' })
  const referredBy = useRef<string>(null);
  const eventTracker = useAnalyticsEventTracker();
  const hasLoadedOnce = useRef<boolean>(false);
  const authLoadingRef = useRef<boolean>(true);
  const { draft, clearDraft } = useDraftPersona();

  useEffect(() => {
    authLoadingRef.current = authLoading;
  }, [authLoading]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (authLoadingRef.current && hasLoadedOnce.current) return;

      if (!hasLoadedOnce.current) hasLoadedOnce.current = true;
      setAuthLoading(true);

      if (user?.isAnonymous) {
        // mark anonymous and bail out
        setIsAnonymousUser(true);
        setAuthLoading(false);
        return;
      }

      if (user) {
        // real login: fetch token + profile
        const accessToken = await user.getIdToken();
        await fetchAndSetAuthUserData(user.uid, accessToken);
        setToken(accessToken);
        setIsAnonymousUser(false);
      } else {
        // fully signed out
        setAuthUser(null);
        setToken(null);
        setIsAnonymousUser(true);
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // print auth user data when updated for debugging
    if (enableDevTools) { console.log('Auth User Data: ', authUser) }
  }, [authUser])

  // code commented: As per new flow we will not store chat history and streaks data when user signup/signin from chat without login
  useEffect(() => {
    if (authUser && !isAnonymousUser && anonymousChat) {
      handleAnonymousChatData();
    }
  }, [authUser?.documentId, isAnonymousUser, anonymousChat]); //eslint-disable-line react-hooks/exhaustive-deps

  // function used to fetch user data from userId and set into the auth state
  const fetchAndSetAuthUserData = async (
    userId: string,
    accessToken: string,
    getAll?: boolean,
  ) => {
    if (userId) {
      const docRef = doc(firestore, 'users', userId);
      const result = await getDoc(docRef);

      if (result?.data()) {
        if (enableDevTools) logging.info('New auth user doc fetched: ', { context: { data: result.data() } })
        const {
          fullName,
          userName,
          shortBio,
          headline,
          ethnicity,
          email,
          profilePhoto,
          profilePicUrl,
          userType,
          index,
          last_indexed_on,
          credits,
          customerId,
          isSubscribed,
          premiumPurchases,
          beta_tester,
          subscription,
          chat_usage,
          verified,
          createdAt,
          updatedAt,
          emailPreferences,
          isTeam,
          google_drive_links,
        } = result?.data();
        let authUserData = {
          uid: result?.id,
          documentId: result?.id,
          fullName: fullName,
          normalizedFullName: fullName?.toLowerCase(),
          userName: userName,
          normalizedUserName: userName?.toLowerCase(),
          shortBio: shortBio,
          headline: headline || "",
          ethnicity: ethnicity || "",
          email: email?.toLowerCase(),
          profilePhoto: profilePhoto,
          profilePicUrl: profilePicUrl,
          userType: userType,
          index: index,
          lastIndexedOn: last_indexed_on?.toDate().toLocaleString(),
          credits: credits,
          customerId: customerId,
          isSubscribed: isSubscribed,
          premiumPurchases: premiumPurchases,
          beta_tester: beta_tester,
          subscription: subscription,
          chat_usage: chat_usage,
          verified: verified,
          createdAt,
          updatedAt,
          emailPreferences,
          isTeam,
          google_drive_links: google_drive_links || [],
        };

        const data = result?.data();
        const combinedData = {
          ...authUserData,
          ...data,
        };

        const userData = modifyUserData(combinedData);
        handleTokenChange(accessToken, userData);
      }
    }
  };

  //function used to sign in with email and password
  const signInWithEmailPassword = (
    email: string,
    password: string,
    contest: boolean = false
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(auth, email, password)
        .then(async (userResponse) => {
          eventTracker('signin-success-email');
          setAuthLoading(false);
          //set anonymous user false if user logged in from chat screen
          if (isAnonymousUser) {
            setIsAnonymousUser(false);
          }
          // Check if welcomeEmailSent is false or not set, and then send the welcome email
          const user_id = userResponse.user.uid
          const userDocRef = doc(firestore, "users", user_id);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists() && userDoc.data().fullName && userDoc.data().welcomeEmailSent === false) {
            if (contest) {
              sendWelcomeFromContestEmail(user_id)
                .then(() => {
                  logging.info("Welcome email sent successfully.");
                  // Don't forget to update the flag in your database to indicate the email has been sent.
                  // await updateDoc(userDocRef, { welcomeEmailSent: true }); <----- happens in the backend
                }).catch((error) => {
                  logging.warning("Error sending welcome email:", { context: { error } });
                });
            } else {
              sendWelcomeEmail(user_id)
                .then(() => {
                  logging.info("Welcome email sent successfully.");
                  // Don't forget to update the flag in your database to indicate the email has been sent.
                  // await updateDoc(userDocRef, { welcomeEmailSent: true }); <----- happens in the backend
                }).catch((error) => {
                  logging.warning("Error sending welcome email:", { context: { error } });
                });
            }
          }

          const freshToken = await userResponse.user.getIdToken(true)
          await fetchAndSetAuthUserData(userResponse.user.uid, freshToken, true)

          resolve(userResponse?.user);
        })
        .catch((error) => {
          eventTracker('signin-failed-email');
          logging.warning("[Sign-In via Email Password] ERROR:", { context: { error } });
          reject(error);
        });
    });
  };

  //function used to create a new user account and signin into the system
  const createUserAccountWithEmailPassword = (userData: UserSignUpFormType) => {
    const { email, password } = userData;


    return new Promise<void>(async (resolve, reject) => {
      try {
        let userCredential;
        let shortFlow = false;
        if (isAnonymousUser && auth.currentUser?.isAnonymous) {
          try {
            // Try to link anonymous user with email and password
            const cred = EmailAuthProvider.credential(email, password);
            userCredential = await linkWithCredential(auth.currentUser!, cred);
            setIsAnonymousUser(false);
            shortFlow = true;
          } catch (error) {
            // If account exists already, just try logging in
            try {
              await signInWithEmailPassword(email, password);
              return;
            } catch (err) {
              logging.error("[Sign-Up] Error linking anonymous user with email:",
                {
                  context: {
                    error,
                    email
                  }
                });
              reject(err);
              return;
            }
          }
        } else {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
        }

        const uid = userCredential.user.uid;
        const userDocRef = doc(firestore, "users", uid);
        let currentTime = serverTimestamp();

        const baseData = {
          email,
          userType: USER_TYPE.FOLLOWER,
          createdAt: currentTime,
          updatedAt: currentTime,
          welcomeEmailSent: false,
          credits: [{ remaining: 10, maxAmount: 10 }],
          verified: true,
          created_via_short_flow: false,
          subscription: {
            pricing_plan: {
              tier: TIER_LEVEL_CONVERSION['free'],
              subscriptionId: '',
              expiration_date: Timestamp.fromMillis(Date.now() + 1000 * 60 * 60 * 24 * 365 * 100),
            },
          },
          emailPreferences: {
            course: true,
            engagement: true,
            inactivity: true,
            new_features: true,
            weekly_updates: true,
            unsubscribeAll: false,
          }
        };

        // Check if draft persona has data - indicates short flow from persona creation
        const hasDraftData = draft.firstName || draft.lastName || draft.email || draft.links.length > 0 || draft.files.length > 0;

        if (hasDraftData && shortFlow) {
          baseData["temp_email"] = email
          baseData.created_via_short_flow = true;
          baseData.userType = USER_TYPE.CREATOR;
          clearDraft(); // Clear draft data from context
        }

        await setDoc(userDocRef, baseData, { merge: true });

        const freshToken = await auth.currentUser.getIdToken(true);
        await fetchAndSetAuthUserData(uid, freshToken, true);

        if (auth.currentUser?.emailVerified) {
          if (shortFlow) {
            eventTracker("signup-success-email-short-flow");
          } else {
            eventTracker("signup-success-email");
          }
        } else {
          if (shortFlow) {
            eventTracker("signup-awaiting-verification-email-short-flow");
          } else {
            eventTracker("signup-awaiting-verification-email");
          }
          await sendEmailVerification(auth.currentUser!);
        }

        resolve();
      } catch (err) {
        logging.error("[Sign-Up via Email Password] ERROR:", { context: err });
        eventTracker("Error creating or linking user");
        reject(err);
      }
    });
  };


  // function create the user and build user persona
  const createCreatorUserAndBuildPersonality = async (
    userData,
    userCredential,
    resolve,
    reject
  ) => {
    const userResponse = await createUpdateUserDataFromUserId(
      userData,
      userCredential?.user?.uid
    );
    // TODO : Set item to be indexed as true only if there are any elements in it
    const itemsToBeIndexed = {
      index_documents: true,
      index_webpage: true,
      index_twitter: true,
      index_yt: true,
      index_sitemap: true,
    };
    if (
      auth?.currentUser?.emailVerified !== undefined &&
      auth.currentUser.emailVerified === false
    ) {
      try {
        await sendEmailVerification(auth.currentUser);
        eventTracker('signup-awaiting_verification-email');
      } catch (error) {
        // Handle error
        logging.error('Error sending verification email during profile creation', {
          context: {
            error,
            userId: userCredential?.user?.uid,
          },
        });
      }
    }

    try { //Calling the index_all api end point after all documents upload/delete is completed
      updatePersona(userCredential?.user?.uid, itemsToBeIndexed)
        .then(async () => {
          const accessToken = await userCredential?.user?.getIdToken();
          await fetchAndSetAuthUserData(userCredential?.user?.uid, accessToken, true);
        }, () => { })
        .catch((err) => {
          logging.error('Error updating persona during profile creation', {
            context: {
              error: err,
              userId: userCredential?.user?.uid,
            },
          });
        });
      eventTracker('create-user');
    } catch (e) {
      eventTracker('create-user failed');
      throw e;
    }

    if (userData?.voiceSamplePath) {
      try { // there is new voice sample
        cloneVoice(userCredential?.user?.uid);
        eventTracker('clone-voice');
      } catch (e) {
        eventTracker('clone-voice failed');
        throw e;
      }
    }

    // //Calling the index_all api end point after all documents upload/delete is completed
    // updatePersona(userCredential?.user?.uid, itemsToBeIndexed);

    if (userResponse) {
      const accessToken = await userCredential?.user?.getIdToken();
      await fetchAndSetAuthUserData(userCredential?.user?.uid, accessToken);
      resolve();
    } else {
      reject();
    }
  };

  // updateProfileData function used to update the creators profile data when a creator user create/update MIMIO profile
  const updateUserProfileData = async (
    userData: UserFormType,
    formMode: FormMode = 'create'
  ) => {
    const {
      profilePhoto,
      existingProfilePhoto,
      voiceSample,
      existingVoiceSample,
      existingProfilePicUrl,
      userDocument,
      existingUserDocument,
      deletedUserDocument,
      documentId,
      bannerPhoto,
      existingBannerPhoto,
      existingBannerUrl,
    } = userData;

    if (enableDevTools) logging.info('AuthContext updating userData: ', { context: { userData } })

    if (documentId) {
      // set indexed initial value to false
      const itemsToBeIndexed: { [key: string]: boolean } = {};
      itemsToBeIndexed['index_documents'] = false;
      itemsToBeIndexed['index_webpage'] = false;
      itemsToBeIndexed['index_twitter'] = false;
      itemsToBeIndexed['index_sitemap'] = false;

      let profilePhotoPath = existingProfilePhoto;
      let profilePicUrl = existingProfilePicUrl;
      let userDocumentsPath = existingUserDocument;
      let voiceSamplePath = existingVoiceSample;
      let bannerPhotoPath = existingBannerPhoto;
      let bannerPhotoUrl = existingBannerUrl;

      //check profile photo is uploaded or not, if uploaded save it to cloud storage and store file path in collection
      if (typeof profilePhoto === 'object' && profilePhoto?.name) {
        if (existingProfilePhoto) {
          try {
            const deletedMediaRef = ref(firestorage, existingProfilePhoto);
            await deleteObject(deletedMediaRef);
          } catch (e) {
            console.log(e);
          }
        }

        const uploadResult = await uploadProfilePhoto(documentId, profilePhoto);
        profilePhotoPath = uploadResult.profilePhotoPath;
        profilePicUrl = uploadResult.profilePicUrl;
      }

      //check banner photo is uploaded or not, if uploaded save it to cloud storage and store file path in collection
      if (typeof bannerPhoto === 'object' && bannerPhoto?.name) {
        const uploadResult = await uploadBannerPhoto(documentId, bannerPhoto);
        bannerPhotoPath = uploadResult.bannerPhotoPath;
        bannerPhotoUrl = uploadResult.bannerPhotoUrl;
      }

      // Check if voice sample is added
      if (typeof voiceSample === 'object' && voiceSample?.name) {
        if (existingVoiceSample) {
          // voice sample file changed, need to remove the old existing file
          try {
            const deletedMediaRef = ref(firestorage, existingVoiceSample);
            await deleteObject(deletedMediaRef);
          } catch (e) {
            console.log(e);
          }
        }
        // Upload new voice sample and edit the path
        voiceSamplePath = await uploadVoiceSample(documentId, voiceSample);
      }

      // Deleting the documents from firebase storage
      if (deletedUserDocument?.length >= 1) {
        await Promise.all<boolean>(
          deletedUserDocument.map(async (documentItem: any) => {
            try {
              const deletedMediaRef = ref(firestorage, documentItem?.path);
              await deleteObject(deletedMediaRef);
              return true;
            } catch (err) {
              console.error(err);
              return false;
            }
          })
        );
      }

      // Uploading the new documents. This also takes care of documents that are replaced.
      let documentsPath: Record<any, any>[] | boolean = [];
      if (userDocument && userDocument?.length >= 1) {
        documentsPath = await uploadUserDocuments(userDocument, documentId);
        if (!documentsPath) {
          return;
        }
      }

      let newUserData = {
        ...userData,
        profilePhotoPath,
        voiceSamplePath,
        profilePicUrl: profilePicUrl,
        documentPath: [...userDocumentsPath, ...documentsPath],
        updatedAt: serverTimestamp(),
        bannerPhotoPath,
        bannerPhotoUrl: bannerPhotoUrl,
      };

      //update user data from user id
      const updatedUserData: any = await updateUserDataFromUserId(
        newUserData,
        documentId,
        formMode
      );

      if (enableDevTools) console.log('updatedUserData: ', updatedUserData)

      // set index_twitter flag to when twitter URL is present
      if (updatedUserData?.userDocument?.length >= 1) {
        itemsToBeIndexed['index_documents'] = true;
      }
      // set index_twitter flag to when twitter URL is present
      if (updatedUserData?.twitterURL) {
        itemsToBeIndexed['index_twitter'] = true;
      }
      // set index_webpage flag to when link to content data / user web page data is present
      if (updatedUserData?.userWebPage?.length >= 1) {
        itemsToBeIndexed['index_webpage'] = true;
      }
      // set index_sitemap flag when scrape_webpage data is present
      if (updatedUserData?.scrape_webpage?.length >= 1) {
        itemsToBeIndexed['index_sitemap'] = true;
      }
      // Set index_yt flag when youtube_links are present
      if (updatedUserData?.youtube_links) {
        itemsToBeIndexed['index_yt'] = true;
      }

      if (formMode === 'profileComplete' || formMode === 'create') {
        itemsToBeIndexed['index_messages'] = true;
      }

      //Calling the index_all api end point after all documents upload/delete is completed
      updatePersona(documentId, itemsToBeIndexed)
        .then(async (res) => {
          if (res.ok) {
            toast.success('Data fully processed!');
            await fetchAndSetAuthUserData(documentId, token, true);
          } else {
            // Handle HTTP error responses
            if (res.status === 429) {
              toast.error('Please wait for your previous request to complete before sending a new one.');
            } else {
              // Handle other HTTP errors (400, 500, etc.)
              const errorMessage = await res.text().catch(() => 'An unexpected error occurred');
              toast.error(`Error: ${errorMessage}`);
              console.error('API Error:', res.status, errorMessage);
              logging.error('Error updating persona', { context: res })
            }
          }
        })
        .catch((err) => {
          // Handle network errors, promise rejections, or other unexpected errors
          console.error('Request failed:', err);
          toast.error('Request failed. Please check your connection and try again.');
        });

      if (voiceSample) {
        // there is new voice sample
        cloneVoice(documentId);
      }
      // // Deleting voice is not supported anymore
      // if (voiceSample === null && existingVoiceSample) {
      //   try { // need to remove the existing clone voice
      //     eventTracker('delete-voice');
      //     deleteVoice(documentId)
      //       .then(async (resp) => {
      //         if (resp.status > 204) {
      //           eventTracker('delete-voice failed');
      //         }
      //       }).catch(() =>{
      //         eventTracker('delete-voice failed');
      //       })
      //   } catch (e) {
      //     eventTracker('delete-voice failed');
      //     throw e;
      //   }
      // }

      // get the updated user data after saving from the collection and update the local auth state to set latest data
      if (updatedUserData) {
        const docRef = doc(firestore, 'users', documentId);
        const result = await getDoc(docRef);
        let currentData = result?.data();
        let authUserData = {
          uid: documentId,
          documentId: documentId,
          fullName: updatedUserData?.fullName?.trim(),
          normalizedFullName: updatedUserData?.fullName?.toLowerCase()?.trim(),
          headline: updatedUserData?.headline || "",
          shortBio: updatedUserData?.shortBio,
          profilePhoto: updatedUserData?.profilePhoto,
          profilePicUrl: updatedUserData?.profilePicUrl,
          voiceSample: updatedUserData?.voiceSample,
          userName: userData?.userName?.trim(),
          normalizedUserName: userData?.userName?.toLowerCase()?.trim(),
          email: userData?.email?.toLowerCase()?.trim(),
          userType: userData?.userType,
          index: userData?.index,
          occupation: userData?.occupation,
          gender: userData?.gender,
          ethnicity: updatedUserData?.ethnicity || "",
          age: userData?.age,
          birthPlace: userData?.birthPlace,
          residencePlace: userData?.residencePlace,
          introMessage: userData?.introMessage,
          lastIndexedOn: result?.data()?.last_indexed_on?.toDate().toLocaleString(),
          credits: currentData?.credits,
          customerId: currentData?.customerId,
          isSubscribed: currentData?.isSubscribed,
          premiumPurchases: currentData?.premiumPurchases,
          beta_tester: currentData?.beta_tester,
          subscription: currentData?.subscription,
          chat_usage: currentData?.chat_usage,
          verified: currentData?.verified,
          scrape_webpage: currentData?.scrape_webpage || [],
        };
        for (let key of extendedKeys) {
          if (!authUserData[key] && currentData[key]) {
            authUserData[key] = currentData[key];
          }
        }
        if (formMode === 'create') {
          eventTracker('creator-account-creation');
        } else if (formMode === 'update') {
          eventTracker('creator-account-update');
        }
        setAuthUser(modifyUserData(authUserData));
        return updatedUserData;
      }
    }
  };

  //function used to upload profile picture to cloud storage
  const uploadProfilePhoto = async (userId: string, profilePhoto: any) => {
    const fileExtension = profilePhoto.name.split('.').pop(); // Extracting the file extension
    const profilePhotoPath = `images/${userId}/profile_pic.${fileExtension}`;
    const profilePhotoStorageRef = ref(firestorage, profilePhotoPath);
    await uploadBytes(profilePhotoStorageRef, profilePhoto);
    // Retrieve the download URL
    const profilePicUrl = await getDownloadURL(profilePhotoStorageRef);
    return { profilePhotoPath, profilePicUrl };
  };

  //function used to upload banner picture to cloud storage
  const uploadBannerPhoto = async (userId: string, bannerPhoto: any) => {
    const fileExtension = bannerPhoto.name.split('.').pop(); // Extracting the file extension
    const bannerPhotoPath = `images/${userId}/banner_pic.${fileExtension}`;
    const bannerPhotoStorageRef = ref(firestorage, bannerPhotoPath);
    await uploadBytes(bannerPhotoStorageRef, bannerPhoto);
    // Retrieve the download URL
    const bannerPhotoUrl = await getDownloadURL(bannerPhotoStorageRef);
    return { bannerPhotoPath, bannerPhotoUrl };
  };

  //function used to upload voice samples to cloud storage
  const uploadVoiceSample = async (userId: string, voiceSample: any) => {
    const path = `files/${userId}/voices/${voiceSample.name}`;
    const voiceSampleStorageRef = ref(firestorage, path);
    await uploadBytes(voiceSampleStorageRef, voiceSample);
    return path;
  };

  // upload user selected documents to the firebase cloud and return document array with path
  const uploadUserDocuments = async (userDocument: any, userId: string) => {
    const documentsPath: Record<any, any>[] = [];
    //check user document array has data
    if (userDocument && userDocument?.length >= 1) {
      const timeStamp = Timestamp.fromDate(new Date());
      // get uploaded documents upload status from firebase
      const documentsUploadStatus = await Promise.all<boolean>(
        userDocument.map(async (documentItem: any) => {
          const documentOrgName = documentItem?.name;
          const documentPath = `files/${userId}/${new Date().getTime()}_${documentItem?.name
            }`;
          const documentStorageRef = ref(firestorage, documentPath);
          try {
            await uploadBytes(documentStorageRef, documentItem);
            documentsPath.push({
              name: documentOrgName,
              path: documentPath,
              uploadedAt: timeStamp,
            });
            return true;
          } catch (err) {
            console.error(err);
            return false;
          }
        })
      );
      const rejectedIndex = documentsUploadStatus.findIndex(
        (item: boolean) => !item
      );
      if (rejectedIndex !== -1) {
        return false;
      }
    }
    return documentsPath;
  };

  // create or update creator user profile data from the user id and based on form mode it will create or update the records in collection
  const createUpdateUserDataFromUserId = async (
    userData: Partial<UserFormType> & Partial<UserCreateProfileFormType>,
    userId: string,
    mode: 'create' | 'update' = 'create'
  ) => {
    const {
      fullName,
      userName,
      shortBio,
      email,
      twitterURL,
      linkedInURL,
      instagramURL,
      youtubeURL,
      tiktokURL,
      userWebPage,
      scrape_webpage,
      youtube_links,
      google_drive_links,
      profilePhotoPath,
      profilePicUrl,
      voiceSamplePath,
      documentPath,
      anythingElse,
      createdAt,
      updatedAt,
      userType,
      occupation,
      gender,
      birthPlace,
      residencePlace,
      age,
      ethnicity,
      headline,
      referredBy,
    } = userData;

    try {
      const userFormData = {
        ...(fullName && { fullName: fullName?.trim() }),
        ...(fullName && {
          normalizedFullName: fullName?.toLowerCase()?.trim(),
        }),
        ...(userName && { userName: userName?.trim() }),
        ...(userName && {
          normalizedUserName: userName?.toLowerCase()?.trim(),
        }),
        ...(shortBio && { shortBio: shortBio }),
        ...(headline && { headline: headline }),
        ...(ethnicity && { ethnicity: ethnicity }),
        ...(email && { email: email?.toLowerCase()?.trim() }),
        ...(twitterURL && { twitterURL: twitterURL }),
        ...(linkedInURL && { linkedInURL: linkedInURL }),
        ...(instagramURL && { instagramURL: instagramURL }),
        ...(youtubeURL && { youtubeURL: youtubeURL }),
        ...(tiktokURL && { tiktokURL: tiktokURL }),
        ...(userWebPage && { userWebPage: userWebPage }),
        ...(scrape_webpage && { scrape_webpage: scrape_webpage }),
        ...(youtube_links && { youtube_links: youtube_links }),
        ...(google_drive_links && { google_drive_links: google_drive_links }),
        ...(profilePhotoPath && { profilePhoto: profilePhotoPath }),
        ...(voiceSamplePath && { voiceSample: voiceSamplePath }),
        ...(profilePicUrl && { profilePicUrl: profilePicUrl }),
        ...(documentPath && { userDocument: documentPath }),
        ...(anythingElse && { anythingElse: anythingElse }),
        ...(occupation && { occupation }),
        ...(gender && { gender }),
        ...(birthPlace && { birthPlace }),
        ...(residencePlace && { residencePlace }),
        ...(age && { age: age }),
        ...(createdAt && { createdAt: createdAt }),
        ...(updatedAt && { updatedAt: updatedAt }),
        ...(userType && { userType: userType }),
        ...(referredBy && { referredBy: referredBy })
      };
      if (mode === 'update') {
        await updateDoc(doc(firestore, 'users', userId), userFormData);
      } else {
        await setDoc(doc(firestore, 'users', userId), userFormData);
      }
      return userFormData;
    } catch (error) {
      console.log('create-update user from id error', error);
    }
  };

  // update user details from user document id
  const updateUserDataFromUserId = async (
    userData: UserFormType,
    userId: string,
    formMode: FormMode
  ) => {
    const {
      fullName,
      userName,
      email,
      shortBio,
      headline,
      ethnicity,
      twitterURL,
      linkedInURL,
      instagramURL,
      youtubeURL,
      tiktokURL,
      userWebPage,
      scrape_webpage,
      youtube_links,
      google_drive_links,
      profilePhotoPath,
      voiceSamplePath,
      profilePicUrl,
      documentPath,
      anythingElse,
      updatedAt,
      userType,
      occupation,
      gender,
      birthPlace,
      residencePlace,
      age,
      bannerPhotoPath,
      bannerPhotoUrl,
    } = userData;

    try {
      let userFormData: any = {
        fullName: fullName?.trim(),
        normalizedFullName: fullName?.toLowerCase()?.trim(),
        shortBio: shortBio,
        twitterURL: twitterURL,
        linkedInURL: linkedInURL,
        instagramURL: instagramURL,
        youtubeURL: youtubeURL,
        tiktokURL: tiktokURL,
        userWebPage: userWebPage,
        ...(scrape_webpage !== undefined && { scrape_webpage }),
        ...(youtube_links !== undefined && { youtube_links }),
        ...(google_drive_links !== undefined && { google_drive_links }),
        profilePhoto: profilePhotoPath,
        voiceSample: voiceSamplePath,
        profilePicUrl: profilePicUrl,
        userDocument: documentPath,
        anythingElse: anythingElse,
        updatedAt: updatedAt,
        userType: userType,
        occupation,
        gender,
        birthPlace,
        residencePlace,
        age: age,
        bannerPhoto: bannerPhotoPath,
        bannerPhotoUrl: bannerPhotoUrl,
        headline: headline || "",
        ethnicity: ethnicity || "",
      };

      if (enableIntroMessages && userData?.introMessage) {
        userFormData.introMessage = userData?.introMessage;
      }

      if (formMode === 'profileComplete' || formMode === 'create') {
        userFormData = {
          userName: userName,
          normalizedUserName: userName?.toLowerCase(),
          ...userFormData,
        };
      }
      // console.log('updating data with: ', userFormData, userId);
      await updateDoc(doc(firestore, 'users', userId), userFormData);
      if (formMode === 'profileComplete' || formMode === 'create') {
        userFormData = {
          email: email?.toLowerCase(),
          ...userFormData,
        };
      }
      return userFormData;
    } catch (error) {
      console.log(error);
    }
  };

  // check user exists with username from provided username
  const checkUserExistWithUsername = async (userName: string) => {
    const result = await getDocs(
      query(
        collection(firestore, 'users'),
        where('normalizedUserName', '==', userName.toLocaleLowerCase())
      )
    );
    if (result?.docs.length >= 1) {
      return true;
    }
    return false;
  };

  // check user exists with email from provided email
  const checkUserExistWithEmail = async (email: string) => {
    const result = await getDocs(
      query(
        collection(firestore, 'users'),
        where('email', '==', email.toLowerCase())
      )
    );
    if (result?.docs.length >= 1) {
      return true;
    }
    return false;
  };

  // on token change call the function to update auth user state
  const handleTokenChange = (authToken: string, authUserData: any) => {
    if (authToken) {
      if (enableDevTools) console.log('Updating auth data')
      setToken(authToken);
      setAuthUser(authUserData);
    } else {
      setToken(null);
      setAuthUser(null);
    }
  };

  // logout/expire the local and firebase user session
  const logoutUser = () => {
    return new Promise<void>((resolve, reject) => {
      signOut(auth)
        .then(() => {
          setToken(null);
          setAuthUser(null);
          setAuthLoading(false);
          setIsAnonymousUser(true);
          setAnonymousChat(null);
          setShowProfileCreated(false);
          clearDraft(); // clear draft data from context
          resolve();
        })
        .catch((error) => {
          logging.error('Error during logout', { context: { error } });
          reject(error);
        });
    });
  };

  // send reset password link on email to the provided email if that user registered with email/password auth method only
  const handleSendResetPasswordEmail = (email: string) => {
    return new Promise<void>((resolve, reject) => {
      fetchSignInMethodsForEmail(auth, email)
        .then((response) => {
          if (!response || response.length === 0) {
            reject({
              code: 'auth/user-not-found',
            });
          }
          if (response.includes('password')) {
            sendPasswordResetEmail(auth, email)
              .then(resolve)
              .catch(reject);
          } else {
            reject({
              code: 'custom-error/user-auth-not-allowed-reset-password',
            });
          }
        })
        .catch(reject);
    });
  };

  // // check creator linked with fan or not
  // const checkUserProfileActivation = async (documentId: string) => {
  //   return onSnapshot(
  //     doc(firestore, 'users', documentId),
  //     (snapshot) => {
  //       const userData = snapshot.data();
  //       const { index } = userData || {};
  //       if (userData && authUser?.index !== index) {
  //         const updatedUserData = {
  //           ...authUser,
  //           index: index,
  //         };
  //         //show a dialog box once the profile is created
  //         if ((!authUser?.index || authUser?.index === undefined) && index) {
  //           setShowProfileCreated(true);
  //         }
  //         handleTokenChange(token, updatedUserData);
  //       }
  //     },
  //     (error) => {
  //       console.log(error);
  //     }
  //   );
  // };

  const generateUserName = async (email: string) => {
    let userName = email.slice(0, email.indexOf('@'));
    while (await checkUserExistWithUsername(userName)) {
      userName = userName + Math.floor(Math.random() * 10) + 1;
    }

    return userName;
  };

  // function used to fetch and upload google profile photo to firebase cloud storage and return profile photo path once uploaded
  // To-do: Refactor to move this to a utils file
  const uploadImageFromUrl = async (photoURL: string, userId: string) => {
    const defaultReturn = { profilePhotoPath: '', profilePicUrl: '' };
    try {
      const imgData = await fetch(photoURL);
      if (!imgData) return defaultReturn;

      const imgBlob = await imgData.blob();
      if (!imgBlob) return defaultReturn;

      const fileExtension = imgBlob?.type?.split('/').pop(); // Extracting the file extension
      const profilePhotoPath = `images/${userId}/profile_pic.${fileExtension}`;
      const profilePhotoStorageRef = ref(firestorage, profilePhotoPath);
      await uploadBytes(profilePhotoStorageRef, imgBlob);

      // Retrieve the download URL
      const profilePicUrl = await getDownloadURL(profilePhotoStorageRef);
      return { profilePhotoPath, profilePicUrl };
    } catch (err) {
      console.log(err);
      return defaultReturn;
    }
  };

  // Generic signin sign up for any provider
  // To-do: Refactor to move this to a utils file
  const signInSignUpWithProvider = (
    provider: FirebaseAuthProvider,
    providerName: string
  ): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        let result;
        let shortFlow = false;
        // if we’re currently anonymous, link instead of re-signing in
        if (auth.currentUser?.isAnonymous) {
          try {
            result = await linkWithPopup(auth.currentUser, provider);
            setIsAnonymousUser(false);
            shortFlow = true;
          } catch {
            // If linking fails, fallback to signInWithPopup
            result = await signInWithPopup(auth, provider);
            setIsAnonymousUser(false);
          }
        } else {
          result = await signInWithPopup(auth, provider);
        }

        const { displayName, email, photoURL, uid } = result.user;
        const userDocRef = doc(firestore, "users", uid);
        const snap = await getDoc(userDocRef);
        let currentTime = serverTimestamp();

        if (!snap.exists()) {
          const profileUrl = photoURL
            ? await uploadImageFromUrl(photoURL, uid)
            : { profilePhotoPath: "", profilePicUrl: "" };

          const baseData = {
            fullName: displayName,
            normalizedFullName: displayName?.toLowerCase(),
            email,
            createdAt: currentTime,
            updatedAt: currentTime,
            welcomeEmailSent: false,
            created_via_short_flow: false,
            userType: USER_TYPE.FOLLOWER,
            credits: [{ remaining: 10, maxAmount: 10 }],
            subscription: {
              pricing_plan: {
                tier: TIER_LEVEL_CONVERSION['free'],
                subscriptionId: '',
                expiration_date: Timestamp.fromMillis(Date.now() + 1000 * 60 * 60 * 24 * 365 * 100),
              },
            },
            verified: true,
            profilePhoto: profileUrl.profilePhotoPath,
            profilePhotoPath: profileUrl.profilePhotoPath,
            profilePicUrl: profileUrl.profilePicUrl,
            emailPreferences: {
              course: true,
              engagement: true,
              inactivity: true,
              new_features: true,
              weekly_updates: true,
              unsubscribeAll: false,
            },
          };

          // Check if draft persona has data - indicates short flow from persona creation
          const hasDraftData = draft.firstName || draft.lastName || draft.email || draft.links.length > 0 || draft.files.length > 0;
          if (hasDraftData && shortFlow) {
            baseData["temp_email"] = email
            baseData.created_via_short_flow = true;
            baseData.userType = USER_TYPE.CREATOR;
            clearDraft(); // Clear draft data from context
          }
          await setDoc(userDocRef, baseData, { merge: true });

          if (shortFlow) {
            eventTracker(`signup-success-${providerName}-short-flow`);
          } else {
            eventTracker(`signup-success-${providerName}`);
          }
        } else {
          if (shortFlow) {
            // If we linked the account, add in more provider data
            const profileUrl = photoURL
              ? await uploadImageFromUrl(photoURL, uid)
              : { profilePhotoPath: "", profilePicUrl: "" };

            const baseData = {
              fullName: displayName,
              normalizedFullName: displayName?.toLowerCase(),
              email: email,
              createdAt: currentTime,
              updatedAt: currentTime,
              welcomeEmailSent: false,
              credits: [{ remaining: 10, maxAmount: 10 }],
              subscription: {
                pricing_plan: {
                  tier: TIER_LEVEL_CONVERSION['free'],
                  subscriptionId: '',
                  expiration_date: Timestamp.fromMillis(Date.now() + 1000 * 60 * 60 * 24 * 365 * 100),
                },
              },
              userType: USER_TYPE.CREATOR,
              verified: true,
              profilePhoto: profileUrl.profilePhotoPath,
              profilePhotoPath: profileUrl.profilePhotoPath,
              profilePicUrl: profileUrl.profilePicUrl,
              emailPreferences: {
                course: true,
                engagement: true,
                inactivity: true,
                new_features: true,
                weekly_updates: true,
                unsubscribeAll: false,
              },
            }

            await updateDoc(userDocRef, baseData);

            eventTracker(`signup-success-${providerName}-short-flow`);
          } else {
            eventTracker(`signin-success-${providerName}`);
          }
        }

        const freshToken = await result.user.getIdToken(true);
        await fetchAndSetAuthUserData(uid, freshToken, true);
        resolve();

      } catch (err) {
        eventTracker(`signin-failed-${providerName}`);
        logging.error(`[Sign-In/Sign-Up with ${providerName}] ERROR:`, { context: err });
        reject(err);
      }
    });
  }

  // open signin with google popup with proceed login with google
  const signInSignUpWithGoogle = (): Promise<void> => {
    return signInSignUpWithProvider(googleAuthProvider, 'google');
  };

  const signInSignUpWithTwitter = (): Promise<void> => {
    return signInSignUpWithProvider(twitterAuthProvider, 'twitter');
  };


  useEffect(() => {
    // Initializing Facebook SDK
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: CONFIG.FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: "v17.0",
      });
    };

    (function (d: Document, s: string, id: string) {
      const element = d.getElementsByTagName(s)[0];
      const fjs = element as Element;
      let js = element as any;
      if (d.getElementById(id)) {
        return;
      }
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      if (fjs && fjs.parentNode) {
        fjs.parentNode.insertBefore(js, fjs);
      } else {
        d.head.appendChild(js);
      }
    })(document, 'script', 'facebook-jssdk');
  }, [auth]);

  // Function to check Facebook login status
  const checkFacebookLoginStatus = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      window.FB.getLoginStatus((response) => {
        console.log(response);
        if (response.status === 'connected') {
          // User is already logged into Facebook, proceed to Firebase authentication
          const { accessToken } = response.authResponse;

          // Sign in to Firebase using the Facebook access token
          signInWithCredential(
            auth,
            FacebookAuthProvider.credential(accessToken)
          )
            .then(async (userCredential) => {
              const user = userCredential.user;
              console.log('Successfully signed in with Facebook:', user);
              resolve();
            })
            .catch(reject);
        } else {
          resolve(); // Resolve when not connected or user cancels login
        }
      });
    });
  };

  const signInSignUpWithFacebook = (
    userData: any,
    messages: any
  ): Promise<any> => {
    return new Promise<void>((resolve, reject) => {
      checkFacebookLoginStatus();

      // Initiate Facebook login
      window.FB.login(
        (response) => {
          if (response.authResponse) {
            console.log(response);
            // User is logged in with Facebook, now authenticate with Firebase
            const { accessToken } = response.authResponse;

            // Sign in to Firebase using the Facebook access token
            signInWithCredential(
              auth,
              FacebookAuthProvider.credential(accessToken)
            )
              .then(async (userCredential) => {
                const user = userCredential.user;
                console.log('Successfully signed in with Facebook: ', user);

                // Send Facebook accesss token and other data to server
                const { displayName, email, uid } = user;
                try {
                  uploadFacebookAuthResponse({
                    ...response.authResponse,
                    since: null,
                    until: null,
                    displayName: displayName,
                    email: email,
                    uid: uid,
                  });
                  eventTracker('sign-in-with-facebook');
                } catch (e) {
                  eventTracker('sign-in-with-facebook failed');
                  throw e;
                }

                const isEmailExists = await checkUserExistWithEmail(email);
                if (!isEmailExists) {
                  if (userData?.userType === USER_TYPE.FOLLOWER) {
                    const userName = await generateUserName(email);
                    userData = {
                      ...userData,
                      userName: userName,
                      normalizedUserName: userName?.toLowerCase(),
                    };
                  }
                  userData = {
                    fullName: displayName,
                    normalizedFullName: displayName?.toLowerCase(),
                    email: email,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    facebookAuthorised: true,
                    ...userData,
                  };
                  createCreatorUserAndBuildPersonality(
                    userData,
                    userCredential,
                    resolve,
                    reject
                  );
                }
              })
              .catch(reject);
          } else {
            // User canceled Facebook login
            console.log('Facebook login canceled by user');
            reject();
          }
        },
        {
          scope:
            'public_profile,email,pages_show_list,pages_read_engagement,pages_read_user_content,instagram_basic,pages_manage_metadata,instagram_manage_comments,instagram_manage_insights',
        }
      );

      // Once Facebok permissions resolved we can use method similar to google sign in
    });
  };

  // store chat and manage streaks when any anonymous user signin/signup from chat screen
  const handleAnonymousChatData = async () => {
    if (anonymousChat?.messages && authUser) {
      try {
        const creatorId = anonymousChat.creatorId;
        const userId = authUser.documentId;
        const newRoomId = `${creatorId}.${userId}`;

        const promises = anonymousChat.messages.map(async (messageItem) => {
          const docId = messageItem.documentId || messageItem.msgId;
          if (docId) {
            const updateData: any = {
              roomId: newRoomId,
              human_id: userId,
              ai_id: creatorId,
            };

            if (messageItem.userType !== 'bot') {
              updateData.senderId = userId;
              updateData.userType = authUser.userType;
            }

            return updateMessagesData(docId, updateData);
          }
        });

        await Promise.all(promises);
        setResetAnonymousData(true);
        setAnonymousChat(null);
      } catch (error) {
        console.log('error saving messages data', error);
      }
    }
  };


  const updateVerifiedStatus = async (userId: string, verified: boolean) => {
    const userDocRef = doc(firestore, 'users', userId);
    try {
      await updateDoc(userDocRef, { verified });
      // Update local authUser state with new verified status
      setAuthUser((currentUser) => ({ ...currentUser, verified }));
    } catch (error) {
      console.error('Error updating verified status:', error);
    }
  };

  const fetchVerifiedStatus = async (userId) => {
    const userDocRef = doc(firestore, "users", userId);
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      const userData = docSnap.data();
      // Return true if the verified field doesn't exist or if it's true
      return userData.verified !== false;
    }
    return true; // Default to true if document doesn't exist
  };

  const updateCreatedProfileData = async (
    userData: UserCreateProfileFormType,
    formMode: FormMode,
  ) => {
    const { documentId, profilePhoto } = userData;

    if (documentId) {
      let profilePhotoPath = '';
      let profilePicUrl = '';

      if (typeof profilePhoto === 'object' && profilePhoto?.name) {
        const uploadResult = await uploadProfilePhoto(documentId, profilePhoto);
        profilePhotoPath = uploadResult.profilePhotoPath;
        profilePicUrl = uploadResult.profilePicUrl;
      }

      const updatedUserData: any = await createUpdateUserDataFromUserId(
        {
          ...userData,
          profilePhotoPath,
          profilePicUrl: profilePicUrl,
          updatedAt: serverTimestamp(),
        },
        documentId,
        'update'
      );

      if (updatedUserData) {
        const authUserData = {
          ...authUser,
          ...updatedUserData,
        };
        if (formMode === 'create') {
          eventTracker('fan-account-creation');
        } else if (formMode === 'update') {
          eventTracker('fan-account-update');
        }
        setAuthUser(modifyUserData(authUserData));
        return updatedUserData;
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        signInWithEmailPassword,
        checkUserExistWithUsername,
        checkUserExistWithEmail,
        logoutUser,
        setAuthUser,
        handleSendResetPasswordEmail,
        // checkUserProfileActivation,
        signInSignUpWithGoogle,
        signInSignUpWithFacebook,
        signInSignUpWithTwitter,
        token,
        authUser,
        authLoading,
        updateUserProfileData,
        isAnonymousUser,
        setIsAnonymousUser,
        anonymousChat,
        setAnonymousChat,
        resetAnonymousData,
        setResetAnonymousData,
        generateUserName,
        createUserAccountWithEmailPassword,
        updateCreatedProfileData,
        showProfileCreated,
        setShowProfileCreated,
        updateVerifiedStatus,
        fetchVerifiedStatus,
        fetchAndSetAuthUserData,
        loginModalVisible,
        setLoginModalVisible,
        referredBy,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default memo(AuthProvider);
